/**
 * AccessDenied — the full-page "not authorized" screen (both auth modes). Shown by the
 * AuthorizationGate when `GET /api/v1/me` returns 403: the caller has a valid credential (a B2C
 * token, or the basic-auth credential) but is NOT a resolved/entitled platform user, so the app
 * shell must NEVER render for them. This is the AUTHORIZATION door the app was missing.
 *
 * Identity source:
 *   - b2c mode: the MSAL account (name/username), loaded via a DYNAMIC import of authService so
 *     MSAL is never pulled into the basic-mode bundle. Sign out ends the real B2C session.
 *   - basic mode: the session principal is null on a 403, so no name is shown; "Sign out" returns
 *     the user to the app origin (the dev credential is baked into the bundle, so no session to end).
 *
 * No PHI here (operator identity only). No em-dash / no emoji (design-law guard).
 */
import { useEffect, useState } from "react";
import { SkyButton } from "@skypointcloud/skypoint-ui";
import { ShieldAlert } from "lucide-react";
import { isB2cMode } from "./authMode";
import { useSession } from "../lib/session";

export function AccessDenied() {
  const { principal } = useSession();
  const [b2cIdentity, setB2cIdentity] = useState<{ name?: string; email?: string } | null>(null);

  useEffect(() => {
    if (!isB2cMode()) return;
    let cancelled = false;
    // Dynamic import keeps MSAL/token-service out of the basic-mode bundle (mirrors api.ts).
    void import("./authService")
      .then(({ getActiveAccount }) => {
        if (cancelled) return;
        const account = getActiveAccount();
        setB2cIdentity({ name: account?.name, email: account?.username });
      })
      .catch((err) => {
        // Identity is non-critical (name/email only), but keep the failure observable, not silent.
        // Dynamic import of reportMsalError so basic mode never loads MSAL.
        void import("./msalConfig").then((m) => m.reportMsalError("accessDeniedIdentity", err)).catch(() => {});
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const name = b2cIdentity?.name ?? principal?.name;
  const email = b2cIdentity?.email ?? principal?.email;

  const handleSignOut = () => {
    if (isB2cMode()) {
      // Real B2C session end (MSAL logoutRedirect). Dynamic import so basic mode never loads MSAL.
      void import("./authService")
        .then(({ logout }) => logout())
        .catch((err) => {
          // Never a dead Sign out: surface the failure (non-PII telemetry) and hard-nav to origin
          // as a fail-safe so the button always does something.
          void import("./msalConfig").then((m) => m.reportMsalError("accessDeniedSignOut", err)).catch(() => {});
          window.location.assign(window.location.origin);
        });
    } else {
      // Basic mode: the dev credential is baked into the bundle, so there is no server session to
      // end. Returning the user to the app origin is the sensible sign-out action here.
      window.location.assign(window.location.origin);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        padding: 24,
        fontFamily: "system-ui",
        background: "#f6f7f9",
      }}
    >
      <div
        role="alert"
        style={{
          maxWidth: 460,
          width: "100%",
          background: "#fff",
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          padding: 32,
          textAlign: "center",
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        }}
      >
        <ShieldAlert size={40} color="#b42318" style={{ margin: "0 auto 16px" }} />
        <h1 style={{ fontSize: 20, fontWeight: 600, color: "#101828", margin: "0 0 8px" }}>
          You do not have access to skyCare
        </h1>
        <p style={{ fontSize: 14, color: "#475467", lineHeight: 1.5, margin: "0 0 4px" }}>
          Your sign in worked, but this account is not set up to use skyCare.
        </p>
        <p style={{ fontSize: 14, color: "#475467", lineHeight: 1.5, margin: "0 0 20px" }}>
          Please contact your administrator to request access.
        </p>
        {(name || email) && (
          <div
            style={{
              fontSize: 13,
              color: "#667085",
              background: "#f9fafb",
              border: "1px solid #eaecf0",
              borderRadius: 8,
              padding: "10px 12px",
              margin: "0 0 20px",
              wordBreak: "break-word",
            }}
          >
            {name && <div style={{ fontWeight: 500, color: "#344054" }}>{name}</div>}
            {email && <div>{email}</div>}
          </div>
        )}
        <SkyButton appearance="primary" onClick={handleSignOut}>
          Sign out
        </SkyButton>
      </div>
    </div>
  );
}
